import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon } from 'lucide-react';
import { useId, useState, type JSX, type ReactNode } from 'react';

import { Card, CardContent, CardHeader } from '@/components/ui/card.js';
import {
  generationContextQueryOptions,
  generationTraceQueryOptions,
  structuredSeedQueryOptions,
} from '@/lib/api/quality.js';
import type {
  ExploratoryLaunchSnapshot,
  GenerationContextDestination,
  GenerationContextManifest,
  ResolvedContextScopeSnapshot,
  StructuredSeedPrompt,
  StructuredSeedRef,
} from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';

import {
  hasManifestContent,
  parseStructuredSeedPrompt,
  resolveStructuredSeedKey,
} from './generation-context.js';
import {
  enrichTraceSteps,
  parseGenerationTraceNdjson,
  redactUrlForDisplay,
  resolveGenerationTraceKey,
  type TraceViewItem,
} from './generation-trace.js';
import { ExploratoryTrace } from './ExploratoryTrace.js';

export function GenerationTrace({
  runId,
  artifactKeys = [],
  variant = 'generation',
  environmentName,
  launchSnapshot,
  terminal = true,
}: {
  runId: string;
  artifactKeys?: string[];
  variant?: 'generation' | 'exploratory';
  environmentName?: string | null;
  launchSnapshot?: ExploratoryLaunchSnapshot | null;
  terminal?: boolean;
}): JSX.Element | null {
  return variant === 'exploratory' ? (
    <ExploratoryTrace
      runId={runId}
      environmentName={environmentName}
      launchSnapshot={launchSnapshot}
      artifactKeys={artifactKeys}
      terminal={terminal}
    />
  ) : (
    <GenerationTracePanel runId={runId} artifactKeys={artifactKeys} />
  );
}

/**
 * Post-run "How generated" panel: user Instructions (once), Sources, slim Seed (no prompt
 * body), and a scannable Trace timeline. Collapsed by default; quiet when nothing loads.
 */
function GenerationTracePanel({
  runId,
  artifactKeys = [],
}: {
  runId: string;
  artifactKeys?: string[];
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const bodyId = useId();

  const {
    data: cachedContext,
    isPending: contextPending,
    isFetching: contextFetching,
    isError: contextFailed,
    error: contextError,
  } = useQuery({
    ...generationContextQueryOptions(runId),
    refetchOnMount: 'always',
  });
  const context = contextFetching || contextFailed ? null : cachedContext;
  const seedKey = resolveStructuredSeedKey(context ?? null);
  const {
    data: seedRaw,
    isError: seedFailed,
    error: seedError,
  } = useQuery(structuredSeedQueryOptions(runId, seedKey));
  const seed =
    typeof seedRaw === 'string' ? parseStructuredSeedPrompt(seedRaw) : null;

  const relativeKey = resolveGenerationTraceKey(
    runId,
    artifactKeys,
    context ?? null,
  );
  const {
    data: traceRaw,
    isError: traceFailed,
    error: traceError,
  } = useQuery(
    generationTraceQueryOptions(runId, relativeKey, !contextPending),
  );
  const hasTraceRaw =
    typeof traceRaw === 'string' && traceRaw.trim().length > 0;
  const viewItems =
    open && typeof traceRaw === 'string'
      ? enrichTraceSteps(parseGenerationTraceNdjson(traceRaw))
      : [];

  const instructions = resolveUserInstructions(context?.manifest ?? null, seed);
  const showSources = hasManifestContent(context?.manifest ?? null);
  const resolvedContextScope = context?.resolved_context_scope ?? null;
  const showQueries = (resolvedContextScope?.entries.length ?? 0) > 0;
  const showSeed = seed != null;
  const showTrace = hasTraceRaw;
  const showLoadError = contextFailed || seedFailed || traceFailed;
  if (
    !instructions &&
    !showSources &&
    !showQueries &&
    !showSeed &&
    !showTrace &&
    !showLoadError
  ) {
    return null;
  }

  const loadErrorMessage = [
    contextFailed ? errorMessageOf(contextError, 'generation context') : null,
    seedFailed ? errorMessageOf(seedError, 'seed prompt') : null,
    traceFailed ? errorMessageOf(traceError, 'trace') : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card>
      <CardHeader>
        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex w-full items-center gap-2 text-left text-sm font-semibold"
        >
          <ChevronDownIcon
            className={cn('size-4 transition-transform', open && 'rotate-180')}
            aria-hidden
          />
          How generated
        </button>
      </CardHeader>
      <CardContent id={bodyId} hidden={!open} className="flex flex-col gap-4">
        {open ? (
          <>
            {showLoadError ? (
              <p className="text-[12px] text-destructive" role="alert">
                Failed to load generation details
                {loadErrorMessage ? `: ${loadErrorMessage}` : ''}
              </p>
            ) : null}
            {instructions ? <InstructionsSection text={instructions} /> : null}
            {showSources && context?.manifest ? (
              <SourcesSection manifest={context.manifest} />
            ) : null}
            {showQueries && resolvedContextScope ? (
              <QueriesSection scope={resolvedContextScope} />
            ) : null}
            {showSeed && seed ? (
              <SeedSection seed={seed} manifest={context?.manifest ?? null} />
            ) : null}
            {showTrace ? <TraceStepList items={viewItems} /> : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Prefer manifest instructions; fall back to seed so the user prompt shows once. */
function resolveUserInstructions(
  manifest: GenerationContextManifest | null,
  seed: StructuredSeedPrompt | null,
): string | null {
  const fromManifest = manifest?.instructions?.trim();
  if (fromManifest) return fromManifest;
  const fromSeed = seed?.instructions?.trim();
  return fromSeed || null;
}

function errorMessageOf(error: unknown, label: string): string {
  if (error instanceof Error && error.message.trim()) {
    return `${label} (${error.message})`;
  }
  return label;
}

function SectionTitle({ children }: { children: ReactNode }): JSX.Element {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

function MetaRow({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}): JSX.Element | null {
  if (value == null || value === '') return null;
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-x-2 text-[12px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-foreground">{value}</dd>
    </div>
  );
}

function FileList({
  items,
  ariaLabel,
}: {
  items: Array<{ filename: string; kind?: string; label?: string }>;
  ariaLabel: string;
}): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <ul className="flex flex-col gap-0.5 text-[12px]" aria-label={ariaLabel}>
      {items.map((item, index) => (
        <li
          key={`${index}-${item.filename}-${item.kind ?? ''}-${item.label ?? ''}`}
        >
          <span className="text-foreground">{item.filename}</span>
          {item.kind ? (
            <span className="text-muted-foreground"> · {item.kind}</span>
          ) : null}
          {item.label ? (
            <span className="text-muted-foreground"> · {item.label}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function InstructionsSection({ text }: { text: string }): JSX.Element {
  return (
    <section className="flex flex-col gap-2" aria-label="Instructions">
      <SectionTitle>Instructions</SectionTitle>
      <p className="whitespace-pre-wrap text-[12px] text-foreground">{text}</p>
    </section>
  );
}

/** Human label for a destination's provider — the same four names `runOutputTargetLabel` uses for
 *  a `Run`, but keyed off the manifest's own typed field rather than a run's source links. The
 *  response is type-cast, not validated, so a provider newer than this build's union falls back to
 *  a neutral phrase rather than disappearing from the Destination row (`.filter(Boolean)` at the
 *  call site would otherwise hide it entirely). */
export function destinationProviderLabel(
  provider: GenerationContextDestination['provider'],
): string {
  switch (provider) {
    case 'azure':
      return 'Azure DevOps';
    case 'zephyr':
      return 'Zephyr Scale';
    case 'xray':
      return 'Xray';
    case 'testrail':
      return 'TestRail';
    case 'own-tms':
      return 'Quality';
    default:
      return 'the test management system';
  }
}

/** The provider-specific ids a destination carries, in display order — each provider only ever
 *  populates the subset of the manifest's shared id fields that applies to it. */
function destinationDetail(
  destination: GenerationContextDestination,
): string | null {
  const parts: string[] = [];
  if (destination.project_id) parts.push(`project ${destination.project_id}`);
  switch (destination.provider) {
    case 'testrail':
      if (destination.section_id != null) {
        parts.push(`section ${destination.section_id}`);
      }
      break;
    case 'zephyr':
      if (destination.folder_id) parts.push(`folder ${destination.folder_id}`);
      break;
    case 'azure':
      if (destination.plan_id) parts.push(`plan ${destination.plan_id}`);
      if (destination.suite_id) parts.push(`suite ${destination.suite_id}`);
      break;
    case 'own-tms':
      if (destination.suite_id) parts.push(`suite ${destination.suite_id}`);
      break;
    case 'xray':
      if (destination.folder_path) {
        parts.push(`folder ${destination.folder_path}`);
      }
      if (destination.test_set_id) {
        parts.push(`test set ${destination.test_set_id}`);
      }
      if (destination.test_plan_id) {
        parts.push(`test plan ${destination.test_plan_id}`);
      }
      break;
  }
  return parts.length > 0 ? parts.join(', ') : null;
}

function SourcesSection({
  manifest,
}: {
  manifest: GenerationContextManifest;
}): JSX.Element {
  return (
    <section className="flex flex-col gap-2" aria-label="Sources">
      <SectionTitle>Sources</SectionTitle>
      <dl className="flex flex-col gap-1">
        <MetaRow label="Mode" value={manifest.mode} />
        <MetaRow label="Platform" value={manifest.platform} />
        <MetaRow label="Model" value={manifest.claude_model} />
        <MetaRow
          label="Case cap (absolute maximum)"
          value={manifest.max_cases != null ? String(manifest.max_cases) : null}
        />
        <MetaRow
          label="Live app"
          value={
            manifest.live_app?.start_url ||
            manifest.live_app?.credential_login_as
              ? [
                  manifest.live_app.start_url
                    ? redactUrlForDisplay(manifest.live_app.start_url)
                    : null,
                  manifest.live_app.credential_login_as
                    ? `as ${manifest.live_app.credential_login_as}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : null
          }
        />
        <MetaRow
          label="Destination"
          value={
            manifest.destination
              ? [
                  destinationProviderLabel(manifest.destination.provider),
                  destinationDetail(manifest.destination),
                ]
                  .filter(Boolean)
                  .join(' · ')
              : null
          }
        />
      </dl>
      <FileList
        ariaLabel="Documents"
        items={(manifest.documents ?? []).map((document) => ({
          filename: document.filename,
          kind: document.kind,
        }))}
      />
      <FileList
        ariaLabel="Context sources"
        items={(manifest.context_sources ?? []).map((source) => ({
          filename: source.staged === false ? '(not fetched)' : source.filename,
          kind: source.kind,
          label: source.label,
        }))}
      />
      <FileList
        ariaLabel="Existing tests"
        items={(manifest.existing_tests ?? []).map((test) => ({
          filename: test.filename,
        }))}
      />
    </section>
  );
}

/**
 * Per-source compiled query (JQL/CQL/WIQL), as the run actually read it — shown for a successful
 * generation run once staging produced a proposal. Distinct from the "Context sources" file list
 * above: this renders the query text itself, not just which source produced a document.
 */
function QueriesSection({
  scope,
}: {
  scope: ResolvedContextScopeSnapshot;
}): JSX.Element {
  return (
    <section className="flex flex-col gap-2" aria-label="Queries">
      <SectionTitle>Queries</SectionTitle>
      <ul
        className="flex flex-col gap-2 text-[12px]"
        aria-label="Resolved queries"
      >
        {scope.entries.map((entry, index) => (
          <li
            key={`${entry.kind}-${entry.label}-${index}`}
            className="flex flex-col gap-0.5"
          >
            <div className="text-foreground">
              {entry.label}
              <span className="text-muted-foreground"> · {entry.kind}</span>
              {entry.origin === 'prompt' ? (
                <span className="text-muted-foreground"> · from prompt</span>
              ) : null}
            </div>
            <pre className="whitespace-pre-wrap break-words rounded-md border-l-2 border-border bg-muted/40 p-2 font-mono text-[11px] text-foreground">
              {entry.query}
            </pre>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SeedRefList({
  refs,
  ariaLabel,
}: {
  refs: StructuredSeedRef[];
  ariaLabel: string;
}): JSX.Element | null {
  if (refs.length === 0) return null;
  return (
    <ul className="flex flex-col gap-0.5 text-[12px]" aria-label={ariaLabel}>
      {refs.map((ref) => (
        <li key={`${ref.prompt_path_basename}-${ref.artifact_key}`}>
          <span className="text-foreground">{ref.prompt_path_basename}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Slim seed metadata only — no template name, max-cases prose, instructions, or scrubbed MD.
 */
function SeedSection({
  seed,
  manifest,
}: {
  seed: StructuredSeedPrompt;
  manifest: GenerationContextManifest | null;
}): JSX.Element {
  const loginSummary = seed.login.required
    ? [
        seed.login.login_as ? `as ${seed.login.login_as}` : 'required',
        seed.login.password_present ? 'password present' : 'no password',
      ].join(' · ')
    : 'not required';

  const manifestUrl = manifest?.live_app?.start_url
    ? redactUrlForDisplay(manifest.live_app.start_url)
    : '';
  const seedUrl = seed.live_app?.start_url_redacted ?? null;
  const showSeedLiveApp = Boolean(seedUrl && seedUrl !== manifestUrl);

  return (
    <section className="flex flex-col gap-2" aria-label="Seed">
      <SectionTitle>
        Seed
        {seed.version ? ` · v${seed.version}` : ''}
        {seed.mode ? ` · ${seed.mode}` : ''}
      </SectionTitle>
      <dl className="flex flex-col gap-1">
        <MetaRow label="Login" value={loginSummary} />
        <MetaRow label="Live app" value={showSeedLiveApp ? seedUrl : null} />
      </dl>
      <SeedRefList refs={seed.document_refs} ariaLabel="Seed document refs" />
      <SeedRefList
        refs={seed.existing_tests_refs}
        ariaLabel="Seed existing-tests refs"
      />
      {seed.revision ? (
        <div className="text-[12px] text-muted-foreground">
          Revision indices:{' '}
          {seed.revision.revise_indices.length > 0
            ? seed.revision.revise_indices.join(', ')
            : 'none'}
        </div>
      ) : null}
    </section>
  );
}

function TraceStepList({ items }: { items: TraceViewItem[] }): JSX.Element {
  const actionCount = items.filter((item) => item.kind === 'action').length;
  const noteCount = items.filter((item) => item.kind === 'narration').length;

  return (
    <section className="flex flex-col gap-2" aria-label="Trace">
      <div className="flex items-baseline justify-between gap-2">
        <SectionTitle>Trace</SectionTitle>
        {items.length > 0 ? (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {actionCount} actions
            {noteCount > 0 ? ` · ${noteCount} notes` : ''}
          </span>
        ) : null}
      </div>
      {items.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">
          No steps in this trace.
        </p>
      ) : (
        <ol className="flex max-h-96 flex-col gap-1.5 overflow-y-auto">
          {items.map((item, index) => (
            <li key={`${item.kind}-${index}`}>
              {item.kind === 'action' ? (
                <TraceActionRow label={item.label} />
              ) : null}
              {item.kind === 'narration' ? (
                <TraceNarrationBlock item={item} />
              ) : null}
              {item.kind === 'final_cases' ? (
                <TraceFinalCasesBlock item={item} />
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function TraceActionRow({ label }: { label: string }): JSX.Element {
  return (
    <div
      className="border-l border-border pl-2 text-[12px] text-foreground"
      title={label}
    >
      · {label}
    </div>
  );
}

function TraceNarrationBlock({
  item,
}: {
  item: Extract<TraceViewItem, { kind: 'narration' }>;
}): JSX.Element {
  const [expanded, setExpanded] = useState(!item.collapsedByDefault);
  if (!item.collapsedByDefault) {
    return (
      <p className="whitespace-pre-wrap py-0.5 text-[12px] text-muted-foreground">
        {item.text}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setExpanded((wasOpen) => !wasOpen)}
        aria-expanded={expanded}
        className="flex w-fit items-center gap-1 text-left text-[12px] text-muted-foreground hover:text-foreground"
      >
        <ChevronDownIcon
          className={cn(
            'size-3 transition-transform',
            expanded && 'rotate-180',
          )}
          aria-hidden
        />
        {expanded ? 'Show less' : item.preview || 'Thinking'}
        {!expanded ? (
          <span className="text-[11px]"> · {item.lineCount} lines</span>
        ) : null}
      </button>
      {expanded ? (
        <p className="max-h-48 overflow-auto whitespace-pre-wrap text-[12px] text-muted-foreground">
          {item.text}
        </p>
      ) : null}
    </div>
  );
}

function TraceFinalCasesBlock({
  item,
}: {
  item: Extract<TraceViewItem, { kind: 'final_cases' }>;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setExpanded((wasOpen) => !wasOpen)}
        aria-expanded={expanded}
        className="flex w-fit items-center gap-1 text-left text-[12px] text-muted-foreground hover:text-foreground"
      >
        <ChevronDownIcon
          className={cn(
            'size-3 transition-transform',
            expanded && 'rotate-180',
          )}
          aria-hidden
        />
        Final cases JSON · {item.caseCount} cases
      </button>
      {expanded ? (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border-l-2 border-border bg-muted/40 p-2 font-mono text-[11px] text-foreground">
          {item.text}
        </pre>
      ) : null}
    </div>
  );
}
